<template>
  <div>
    <main-header>
      <v-spacer />
      <v-btn
        :loading="loadingNewUser"
        color="primary"
        class="ma-2 hidden-sm-and-down"
        dark
        @click="onShowNewUser"
      >
        New User
        <v-icon right>
          mdi-plus
        </v-icon>
      </v-btn>
    </main-header>
    <v-container fluid>
      <v-data-table
        :headers="usersTableHeaders"
        :items="itemsList"
        class="elevation-1 table"
        multi-sort
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :loading="loadingTable"
        hide-default-footer
        item-key="id"
        :items-per-page="perPage"
        fixed-header
        @update:sort-by="getItemsListWithSort"
        @update:sort-desc="getItemsListWithSort"
      >
        <template v-slot:top>
          <v-toolbar
            class="toolbar"
            height="84"
            flat
          >
            <div class="toolbar-content-special d-flex align-center w-100">
              <v-spacer />
              <FilterInput
                v-model="filter"
                :items="usersTableFilters"
                filter-range-from-payload-string=">"
                filter-range-to-payload-string="<"
              />
            </div>
          </v-toolbar>
          <v-divider />
        </template>
        <template v-slot:item.roles="{ item }">
          <v-select
            v-model="item.roles"
            :items="allRoles"
            chips
            multiple
            small-chips
            dense
            solo
            hide-details
            @change="onRolesChange(item)"
          />
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex">
            <v-icon
              small
              class="mr-2"
              @click="() => onShowDeleteDialog(item)"
            >
              mdi-trash-can
            </v-icon>
          </div>
        </template>
        <template v-slot:no-data>
          <v-btn
            color="primary"
            dark
            @click="getItemsList"
          >
            Refresh
          </v-btn>
        </template>

        <template v-slot:footer>
          <v-container
            fluid
            class="mt-4"
          >
            <v-row>
              <v-col
                cols="9"
                align-self="center"
              >
                <v-pagination
                  v-model="page"
                  class="b-pagination"
                  :length="totalPages"
                  :total-visible="11"
                  @input="onChangePage"
                />
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="perPage"
                  class="select-per-page"
                  :items="perPageItems"
                  placeholder="Items per page"
                  dance
                  @change="perPage = parseInt($event, 10)"
                />
              </v-col>
            </v-row>
          </v-container>
          <div class="text-center py-2" />
        </template>
      </v-data-table>

      <ModalCreateUser
        v-if="showCreateUserDialog"
        v-model="showCreateUserDialog"
        :roles="allRoles"
        @on-save="getItemsList"
      />

      <ModalDeleteUser
        v-if="showDeleteUserDialog"
        v-model="showDeleteUserDialog"
        :data="deletedItem"
        @delete="getItemsList"
      />
    </v-container>
  </div>
</template>

<script>
// constants
import {usersTableFilters, usersTableHeaders} from "@/utils/staticTablesColumns";
// modules
import {mapGetters} from "vuex";
// components
import MainHeader from "@/components/MainHeader";
import FilterInput from "@/components/FilterInput";
import ModalCreateUser from "@/components/modals/ModalCreateUser";
import ModalDeleteUser from "@/components/modals/ModalDeleteUser";

export default {
  name: "Users",

  components: {
    MainHeader,
    FilterInput,
    ModalCreateUser,
    ModalDeleteUser
  },

  data() {
    return {
      usersTableHeaders: usersTableHeaders,
      usersTableFilters: usersTableFilters,
      editedItem: {},
      deletedItem: {},
      showDeleteUserDialog: false,
      showCreateUserDialog: false,
      loadingNewUser: false,
      restoreFromQuery: false,
      userName: null,
      email: null,
      allRoles: ['VIEWER', 'EDITOR', 'ADMIN'],
      sortBy: [],
      sortDesc: [],
      loadingTable: false,
      perPage: 10,
      page: 1,
      filter: [],
      perPageItems: [10, 25, 50, 100, 500]
    }
  },
  computed: {
    ...mapGetters({
      itemsList: "UsersStore/users",
      pagination: "UsersStore/usersPagination"
    }),

    totalPages() {
      return this.pagination.totalPages || 0;
    },

    filterStringURL: {
      get() {
        return this.filter.map(item => {
          return `${item.key}:${item.value}:${item.name}`;
        }).join(",");
      },
      set(newValue) {
        const filterArray = newValue.split(",");

        filterArray.forEach((item, index) => {
          const filterKeyValue = item.split(":")
          this.filter[index] = {};
          this.filter[index].key = filterKeyValue[0];
          this.filter[index].value = filterKeyValue[1];
          this.filter[index].name = filterKeyValue[2] || "";
        });

        return newValue;
      }
    }
  },

  watch: {
    perPage(newValue) {
      if (this.restoreFromQuery) return;

      this.addQueryToRouteWithReload("perPage", newValue);
      this.addQueryToRouteWithReload("page", 1);
    },
    filter: {
      handler() {
        if (this.restoreFromQuery) return;

        this.getItemsListWithFilters();
      },
      deep: true
    }
  },

  created() {
    const query = this.$route.query;
    const hasQueryUserName = query.userName;

    if (hasQueryUserName) {
      this.userName = query.userName;
    }
    const hasQueryEmail = query.email;

    if (hasQueryEmail) {
      this.email = query.email;
    }

    const payload = {
      page: this.page,
      size: this.perPage,
      sort: this.sortBy.map(
          (val, index) => `${val},${this.sortDesc[index] ? "desc" : "asc"}`
      )
    };
    this.filter.forEach(f => {
      if (f.key && f.value !== undefined && f.value !== null && f.value !== '') {
        payload[f.key] = f.value;
      }
    });
    this.$store.dispatch("UsersStore/getUsers", payload)
        .then(() => {

          this.getItemsList();
        })

    if (this.$route.query) {
      this.restoreFromQuery = true;
      const query = this.$route.query;
      this.perPage = parseInt(query.perPage) || 50;
      this.page = parseInt(query.page) || 1;
      let sort = query.sort || [];

      if (sort.length) {
        if (typeof sort === "string") sort = [sort];

        const sortBy = [];
        const sortDesc = [];
        sort.forEach(item => {
          const array = item.split(",");
          sortBy.push(array[0]);
          sortDesc.push(array[1] !== "asc");
        });
        this.sortBy = sortBy;
        this.sortDesc = sortDesc;
      }

      if (query?.filter?.length) {
        this.filterStringURL = query.filter;
      }

      if (query.userName) {
        this.userName = query.userName;
      }
    }
  },

  methods: {
    async onRolesChange(item) {
      try {
        // Only send roles!
        const patchObj = {roles: item.roles};
        await this.$store.dispatch("UsersStore/patchUser", {id: item.id, ...patchObj});
      } catch (err) {
        this.$toast.error("Failed to update roles!");
      }
    },
    onShowDeleteDialog(item) {
      this.deletedItem = Object.assign({}, item);
      this.showDeleteUserDialog = true;
    },

    onShowNewUser() {
      this.showCreateUserDialog = true;
    },

    async getItemsList() {
      try {
        this.loadingTable = true;

        const payload = {
          page: this.page,
          size: this.perPage,
          sort: this.sortBy.map(
              (val, index) => `${val},${this.sortDesc[index] ? "desc" : "asc"}`
          )
        };
        this.filter.forEach(f => {
          if (f.key && f.value !== undefined && f.value !== null && f.value !== '') {
            payload[f.key] = f.value;
          }
        });
        await this.$store.dispatch("UsersStore/getUsers", payload)
      } finally {
        this.loadingTable = false;
        this.restoreFromQuery = false;
      }
    },

    getItemsListWithSort() {
      if (this.loadingTable) return;
      this.page = 1;
      let sort = this.sortBy.map((val, index) => {
        return `${val},${this.sortDesc[index] ? "desc" : "asc"}`;
      });
      this.addQueryToRouteWithReload("page", 1);
      this.addQueryToRouteWithReload("sort", sort);
    },

    getItemsListWithFilters() {
      this.addQueryToRouteWithReload("filter", this.filterStringURL);
      this.addQueryToRouteWithReload("perPage", this.perPage);
      this.addQueryToRouteWithReload("page", 1);
    },

    addQueryToRouteWithReload(name, value) {
      this.$router.replace({
            query: Object.assign({
              ...this.$route.query
            }, {
              [name]: value
            })
          }
      );
    },

    onChangePage() {
      this.addQueryToRouteWithReload("page", this.page);
    }
  }
}
</script>
